/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';

import { APPLICATION_VIEW_OFFICE } from '@gh/builder-shared-data';
import { createRoute } from '@gh/builder-shared';
import { lazyActivatorRoute } from '@gh/core-lazy';

import { ComponentDetailsTabContainerComponent } from './components/component-details-tab-container/component-details-tab-container.component';
import { ComponentListTabComponent } from './components/component-list-tab/component-list-tab.component';
import { CostCenterDetailsComponent } from './components/cost-center-details/cost-center-details.component';
import { EstimatingCostCenterListTabComponent } from './components/estimating-cost-center-list-tab/estimating-cost-center-list-tab.component';
import { EstimatingImportPriceTabComponent } from './components/estimating-import-price-tab/estimating-import-price-tab.component';
import { EstimatingSalesTaxRateListTabComponent } from './components/estimating-sales-tax-rates-tab/estimating-sales-tax-rate-list-tab.component';
import { EstimatingTabContainerComponent } from './components/estimating-tab-container/estimating-tab-container.component';
import { EstimatingTakeOffParamsTabComponent } from './components/estimating-take-off-params-tab/estimating-take-off-params-tab.component';
import { ExportPricesTabComponent } from './components/export-prices-tab/export-prices-tab.component';
import { ItemDetailsTabContainerComponent } from './components/item-details-tab-container/item-details-tab-container.component';
import { ItemsListTabComponent } from './components/items-list-tab/items-list-tab.component';
import { UomListTabComponent } from './components/uom-list-tab/uom-list-tab.component';
import { ComponentDetailsBusinessService } from './resources/component-details-business.service';
import { ComponentListBusinessService } from './resources/component-list-business.service';
import { CostCenterDetailsBusinessService } from './resources/cost-center-details-business.service';
import { EstimatingCostCenterListTabBusinessService } from './resources/estimating-cost-center-list-tab-business.service';
import { EstimatingImportPriceTabBusinessService } from './resources/estimating-import-price-tab-business.service';
import { EstimatingSalesTaxRatesTabBusinessService } from './resources/estimating-sales-tax-rates-tab-business.service';
import { EstimatingTakeOffParamsTabBusinessService } from './resources/estimating-take-off-params-tab-business.service';
import { ItemDetailsBusinessService } from './resources/item-details-business.service';
import { ItemsListBusinessService } from './resources/items-list-business.service';
import { UomListBusinessService } from './resources/uom-list-business.service';
import { EstimatingFeeExclusionsTabBusinessService } from './resources/estimating-fee-exclusions-tab-business.service';
import { FeeExclusionsListTabComponent } from './components/estimating-fee-exclusions-tab/fee-exclusions-list-tab.component';

const routes = [
  lazyActivatorRoute,
  createRoute({
    component: EstimatingTabContainerComponent,
    path: '',
    children: [
      createRoute({
        component: UomListTabComponent,
        init: UomListBusinessService,
        path: 'uom',
        auth: {
          applicationView: APPLICATION_VIEW_OFFICE,
          permission: [
            'Setup.Estimating.UoM.LIST',
          ],
        },
      }),
      createRoute({
        component: EstimatingTakeOffParamsTabComponent,
        path: 'take-off-params',
        init: EstimatingTakeOffParamsTabBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.Estimating.TakeOffParameters.LIST',
          ],
        },
      }),
      createRoute({
        component: EstimatingSalesTaxRateListTabComponent,
        path: 'sales-tax-rates',
        init: EstimatingSalesTaxRatesTabBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.Estimating.SalesTaxRates.LIST',
          ],
        },
      }),
      createRoute({
        component: EstimatingCostCenterListTabComponent,
        path: 'cost-centers',
        init: EstimatingCostCenterListTabBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.Estimating.CostCenters.LIST',
          ],
        },
      }),
      createRoute({
        component: ItemsListTabComponent,
        path: 'items',
        init: ItemsListBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.Estimating.Items.LIST',
          ],
        },
      }),
      createRoute({
        component: ComponentListTabComponent,
        path: 'components',
        init: ComponentListBusinessService,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.Estimating.Components.LIST',
          ],
        },
      }),
      createRoute({
        component: ExportPricesTabComponent,
        path: 'export-prices',
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.Estimating.Items.LIST',
          ],
        },
      }),
      createRoute({
        component: EstimatingImportPriceTabComponent,
        path: 'import-prices',
        init: EstimatingImportPriceTabBusinessService,
        leave: true,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
          permission: [
            'Setup.Estimating.Import.EXECUTE',
          ],
        },
      }),
      createRoute({
        component: FeeExclusionsListTabComponent,
        path: 'fee-exclusions',
        init: EstimatingFeeExclusionsTabBusinessService,
        leave: true,
        auth: {
          applicationView: [APPLICATION_VIEW_OFFICE],
        },
      }),
    ],
  }),
  createRoute({
    component: CostCenterDetailsComponent,
    path: 'cost-centers/new',
    init: CostCenterDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: [APPLICATION_VIEW_OFFICE],
      permission: [
        'Setup.Estimating.CostCenters.CREATE',
      ],
    },
  }),
  createRoute({
    component: CostCenterDetailsComponent,
    path: 'cost-centers/:costCenterId',
    init: CostCenterDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: [APPLICATION_VIEW_OFFICE],
      permission: [
        'Setup.Estimating.CostCenters.EDIT',
        'Setup.Estimating.CostCenters.VIEW',
      ],
    },
  }),
  createRoute({
    component: ItemDetailsTabContainerComponent,
    path: 'items/new',
    init: ItemDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: [APPLICATION_VIEW_OFFICE],
      permission: [
        'Setup.Estimating.Items.CREATE',
      ],
    },
  }),
  createRoute({
    component: ItemDetailsTabContainerComponent,
    path: 'items/:itemId',
    init: ItemDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: [APPLICATION_VIEW_OFFICE],
      permission: [
        'Setup.Estimating.Items.EDIT',
        'Setup.Estimating.Items.VIEW',
      ],
    },
  }),
  createRoute({
    component: ComponentDetailsTabContainerComponent,
    path: 'components/new',
    init: ComponentDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: [APPLICATION_VIEW_OFFICE],
      permission: [
        'Setup.Estimating.Components.CREATE',
      ],
    },
  }),
  createRoute({
    component: ComponentDetailsTabContainerComponent,
    path: 'components/:componentId',
    init: ComponentDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: [APPLICATION_VIEW_OFFICE],
      permission: [
        'Setup.Estimating.Components.EDIT',
        'Setup.Estimating.Components.VIEW',
      ],
    },
  }),
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class BuilderSetupEstimatingRoutingModule {
}
